import { useMemo } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import { GetClosedCohortQuery } from "generated-graphql/graphql";

type CohortSummaryData = NonNullable<
  NonNullable<GetClosedCohortQuery["cohort"]>["closedCohortSummary"]
>;

interface NewMexicoSummaryProps {
  cohortSummaryData: CohortSummaryData;
}

// NM maximum fee per company
const NM_MAX_FEE = 250;

/**
 * New Mexico state-specific summary component.
 *
 * Fee tiers (per facility):
 * - 1-3 chemicals: $25
 * - 4-6 chemicals: $50
 * - 7-9 chemicals: $75
 * - 10-12 chemicals: $100
 * - 13-15 chemicals: $125
 * - 16-18 chemicals: $150
 * - 19-21 chemicals: $175
 * - 22-24 chemicals: $200
 * - 25-27 chemicals: $225
 * - 28+ chemicals: $250
 *
 * IMPORTANT: Maximum total fee is $250 per company.
 */
export function NewMexicoSummary({ cohortSummaryData }: NewMexicoSummaryProps) {
  const theme = useTheme();

  const { breakdown, subtotal, total } = useMemo(() => {
    const breakdown = cohortSummaryData.nmFacilityFeeBreakdown ?? [];

    // Calculate subtotal (sum of all facility fees)
    const subtotal = breakdown.reduce((sum, item) => sum + item.fee, 0);

    // Cap at NM maximum
    const total = Math.min(subtotal, NM_MAX_FEE);

    return { breakdown, subtotal, total };
  }, [cohortSummaryData.nmFacilityFeeBreakdown]);

  const formatCurrency = (amount: number) => {
    return `$${amount.toFixed(2)}`;
  };

  if (breakdown.length === 0) {
    return null;
  }

  const isOverMax = subtotal > NM_MAX_FEE;

  return (
    <Box
      sx={{
        mt: 3,
        pt: 2,
        borderTop: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mb: 1.5, fontWeight: 500 }}
      >
        New Mexico Annual Fee Calculation
      </Typography>

      <TableContainer>
        <Table size="small" sx={{ minWidth: 400 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Facility</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>
                Chemicals
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>
                Fee
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {breakdown.map((item) => (
              <TableRow key={item.facilityId}>
                <TableCell>{item.facilityName}</TableCell>
                <TableCell align="right">{item.chemicalCount}</TableCell>
                <TableCell align="right">{formatCurrency(item.fee)}</TableCell>
              </TableRow>
            ))}

            {/* Subtotal row (only show if different from total due to cap) */}
            {isOverMax && (
              <TableRow>
                <TableCell
                  colSpan={2}
                  sx={{
                    fontWeight: 600,
                    borderBottom: "none",
                    pt: 2,
                  }}
                >
                  Subtotal
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    fontWeight: 600,
                    borderBottom: "none",
                    pt: 2,
                    textDecoration: "line-through",
                    color: "text.secondary",
                  }}
                >
                  {formatCurrency(subtotal)}
                </TableCell>
              </TableRow>
            )}

            {/* Total row */}
            <TableRow>
              <TableCell
                colSpan={2}
                sx={{
                  fontWeight: 700,
                  fontSize: "1rem",
                  pt: isOverMax ? 0.5 : 2,
                  borderBottom: "none",
                }}
              >
                TOTAL
                {isOverMax && (
                  <Typography
                    component="span"
                    variant="caption"
                    color="text.secondary"
                    sx={{ ml: 1 }}
                  >
                    (NM max $250)
                  </Typography>
                )}
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  fontWeight: 700,
                  fontSize: "1rem",
                  pt: isOverMax ? 0.5 : 2,
                  borderBottom: "none",
                }}
              >
                {formatCurrency(total)}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
